import { Box, Card } from "@mui/material";
import MDBox from "components/MDBox";
import { useAuth } from "context";
import { NavbarAlert } from "examples/Alert";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useState } from "react";
import SelectSmall from "components/MDSelector";
import ImageUploader from "./components/ImageUploader";
import MDButton from "components/MDButton";
import EditOrderDetails from "./components/EditOrderDetails";
import { getRestaurantList, uploadReceipts, getSingleReceipt } from "./_request";
import { useEffect } from "react";
import { ProcessingModal, SuccessModal } from "./components/UploadStatusModal";
import { useNavigate, useLocation } from "react-router-dom";
import SimpleBackdrop from "components/MDLoader_Backdrop";

const UploadReceipts = () => {
  const { showSnackbarWithMessage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Function to extract ID from URL query parameters
  const getReceiptIdFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const keys = Array.from(searchParams.keys());
    // Get the first parameter key as the ID (since format is ?id without key=value)
    if (keys.length > 0) {
      return keys[0];
    }
    return null;
  };

  // Initialize state based on whether there's an ID in URL
  const receiptId = getReceiptIdFromUrl();
  const [step, setStep] = useState(receiptId ? 1 : 0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extractedData, setExtractedData] = useState(null);
  // Updated state for restaurants
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedRestaurantData, setSelectedRestaurantData] = useState(null);
  const [showDoneModal, setShowDoneModal] = useState(false);
  const [loader, setLoader] = useState(receiptId ? true : false);

  const goBack = () => {
    navigate(-1); // go back to the previous page
  };

  const restaurantsData = async () => {
    try {
      const { data } = await getRestaurantList();
      // Transform the data for SelectSmall component
      if (data && Array.isArray(data)) {
        const formattedRestaurants = data.map((restaurant) => ({
          label: `${restaurant.name} - ${restaurant?.storeAddressLine1}`,
          value: restaurant.id,
          originalData: restaurant,
        }));
        setRestaurants(formattedRestaurants);
        return formattedRestaurants;
      }
    } catch (e) {
      console.log(e);
    }
    return [];
  };

  // Function to fetch single receipt data
  const fetchSingleReceipt = async (receiptId, restaurantsList) => {
    try {
      const { data } = await getSingleReceipt(receiptId);
      if (data && data.success) {
        const receiptData = data.data;
        // Set extracted data
        setExtractedData(receiptData);
        // Set restaurant based on receipt data
        if (receiptData._restaurant_store_restaurant_store) {
          const restaurantId = receiptData._restaurant_store_restaurant_store.id;
          setSelectedRestaurant(restaurantId);

          // Find and set restaurant data
          const restaurantInfo = restaurantsList.find(
            (restaurant) => restaurant.value === restaurantId
          );
          if (restaurantInfo) {
            setSelectedRestaurantData(restaurantInfo.originalData);
          }
        }

        // Keep step at 1 (already initialized)
      } else {
        showSnackbarWithMessage({
          type: "error",
          message: "Failed to load receipt data",
        });
        // Reset to step 0 on error
        setStep(0);
      }
    } catch (error) {
      console.error("Error fetching receipt:", error);
      showSnackbarWithMessage({
        type: "error",
        message: "Failed to load receipt data. Please try again.",
      });
      // Reset to step 0 on error
      setStep(0);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    const initializeComponent = async () => {
      // First, load restaurants
      const restaurantsList = await restaurantsData();

      // Check if there's an ID in the URL
      const receiptId = getReceiptIdFromUrl();
      if (receiptId) {
        // If ID exists, fetch the receipt data
        await fetchSingleReceipt(receiptId, restaurantsList);
      }
    };

    initializeComponent();
  }, [location.search]);

  const submitReceipts = async (e) => {
    e.preventDefault();
    // Validation
    if (uploadedFiles.length === 0) {
      showSnackbarWithMessage({
        type: "error",
        message: "Please upload at least one receipt",
      });
      return;
    }
    // Validate restaurant selection
    if (!selectedRestaurant) {
      showSnackbarWithMessage({
        type: "error",
        message: "Please select a restaurant",
      });
      return;
    }
    // Check if all files are fully uploaded
    const incompleteFiles = uploadedFiles.filter((file) => file.status !== "completed");
    if (incompleteFiles.length > 0) {
      showSnackbarWithMessage({
        type: "error",
        message: "Please wait for all files to finish uploading",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Extract the actual File objects from uploadedFiles
      const filesToSubmit = uploadedFiles.map((uploadedFile) => uploadedFile.file);
      const {
        data: { success, data, message },
      } = await uploadReceipts(filesToSubmit, selectedRestaurant); // Use selectedRestaurant ID
      if (success && data) {
        setExtractedData(data);
        setStep(1);
      } else {
        showSnackbarWithMessage(
          {
            type: "error",
            message: message ? message : "An Error occured, please try again",
            width: 400,
          },
          5000
        );
      }
    } catch (error) {
      showSnackbarWithMessage({
        type: "error",
        message: "Failed to upload receipts. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestaurantChange = (event) => {
    setSelectedRestaurant(event.target.value);
    // Find and store the selected restaurant's full data
    const selectedRestaurantInfo = restaurants.find(
      (restaurant) => restaurant.value === event.target.value
    );

    if (selectedRestaurantInfo) {
      setSelectedRestaurantData(selectedRestaurantInfo.originalData);
    }
  };

  return (
    <>
      <DashboardNavbar />
      <NavbarAlert />
      <DashboardLayout>
        <MDBox
          py={1}
          sx={{
            mt: 10,
            "@media (max-width:767px)": {
              mt: 8,
            },
          }}
        >
          <Card
            sx={{
              p: 4,
              borderRadius: "8px",
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
              "@media (max-width:767px)": {
                p: 2,
              },
            }}
          >
            <div className="flex flex-col md:justify-center md:items-center">
              <div className="md:w-[480px]">
                {step == 0 ? (
                  <>
                    <div>
                      <div className="text-lg text-[#181D27] font-semibold">Upload receipt</div>
                      <p className="text-sm ">
                        Upload receipts of your order to earn reward points on that order
                      </p>
                    </div>

                    <form onSubmit={submitReceipts} className="mt-8">
                      <div className="flex flex-col">
                        <label className="mb-[6px] leading-[20px] text-[#414651] text-[14px] font-medium">
                          Select Restaurant
                        </label>
                        <SelectSmall
                          placeholder="Select a Restaurant"
                          menuItems={restaurants}
                          filterValue={selectedRestaurant}
                          customHandler={handleRestaurantChange}
                          setFilterValue={setSelectedRestaurant}
                        />
                        <span className="text-[13px] text-[#475467] mt-[2px]">
                          Choose the restaurant this receipt belongs to.
                        </span>
                      </div>

                      <div className="mt-8">
                        <ImageUploader
                          uploadedFiles={uploadedFiles}
                          setUploadedFiles={setUploadedFiles}
                        />
                        <p className="text-[13px] leading-[19px] text-[#475467] mt-4">
                          Ensure Restaurant details, Order ID, Delivery date and Sub-total Amount
                          are duly captured.
                        </p>
                      </div>

                      {/* BACK AND NEXT BTN  */}
                      <Box
                        display="flex"
                        className="gap-2 md:gap-4"
                        alignItems="center"
                        style={{ marginTop: "50px" }}
                      >
                        <MDButton
                          fullWidth
                          variant="outlined"
                          color="black"
                          onClick={goBack}
                          sx={{
                            borderRadius: "8px",
                            padding: "10px",
                            textTransform: "none",
                            border: "1px solid #D0D5DD",
                            textDecoration: "lowercase",
                            fontSize: 14,
                            boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                          }}
                        >
                          Back
                        </MDButton>

                        <MDButton
                          type="submit"
                          sx={{ textTransform: "none", fontSize: 14 }}
                          fullWidth
                          variant="contained"
                          color="success"
                          disabled={isSubmitting || !selectedRestaurant}
                        >
                          {"Continue"}
                        </MDButton>
                      </Box>
                    </form>
                  </>
                ) : (
                  <EditOrderDetails
                    setStep={setStep}
                    extractedData={extractedData}
                    setShowDoneModal={setShowDoneModal}
                    selectedRestaurantData={selectedRestaurantData}
                  />
                )}
              </div>
            </div>
          </Card>
        </MDBox>

        {/* MODALS  */}
        <ProcessingModal open={isSubmitting} />
        <SuccessModal goBack={goBack} open={showDoneModal} />

        {/* Loading backdrop for getSingleReceipt */}
        {loader && <SimpleBackdrop />}
      </DashboardLayout>
    </>
  );
};

export default UploadReceipts;
